import { ScheduledEventPlatformEntry } from "@kaltura/mediaspace-shared-types";
import { translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { TruncateWithTooltip } from "@kaltura/mediaspace-shared-ui";
import { ReactNode, useState } from "react";
import styled from "@emotion/styled";
import { Tooltip, Typography } from "@kaltura/ds-react-components";
import { ActionIconButton } from "../event-list-item/action-section/StyledComponents";
import { Info24Icon } from "@kaltura/ds-react-icons";
import EventListItemModal from "../event-list-item-modal/EventListItemModal";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { useEventListTimeFormatting } from "../useEventListTimeFormatting";

export interface SlimEventListItemProps {
    /**
     * The list entry data.
     */
    entry: ScheduledEventPlatformEntry;
    /**
     * Optional call-to-action button to be displayed.
     */
    callToActionButton?: ReactNode;
    /**
     * Optional flag indicating if the entry is in the watchlist.
     * This indication will pe presented only in entry details modal.
     */
    inWatchlist?: boolean;
    /**
     * Optional function to handle toggling the watchlist status
     */
    onToggleWatchList?: (id: string, add: boolean) => void;
    /**
     * Optional callback invoked when more info clicked.
     */
    onShowInfo?: () => void;
}

const Thumbnail = styled("img")(({ theme }) => ({
    width: 190,
    height: 106,
    borderRadius: theme.kaltura.shape.roundness2,
}));

const ItemContainer = styled("div")(({ theme }) => ({
    display: "flex",
    padding: theme.spacing(3),
    borderRadius: theme.kaltura.shape.roundness2,
    backgroundColor: theme.kaltura.palette.surfaces.paper,
    justifyContent: "space-between",
}));

const ItemInfoAndActions = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "128px",
    paddingRight: theme.spacing(3),
}));

const DateLabel = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone2,
    paddingBottom: theme.spacing(1),
}));

const Actions = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
}));

/**
 * SlimEventListItem component
 *
 * This component displays a slim version of an event list item with a thumbnail.
 * It will render short entry details (name and date),
 * configurable action and more info button which opens a modal with event item full details.
 *
 */
export const SlimEventListItem = ({
    entry,
    callToActionButton,
    inWatchlist,
    onToggleWatchList,
    onShowInfo,
}: SlimEventListItemProps) => {
    const [showReadMoreModal, setShowReadMoreModal] = useState(false);

    const { formattedDate, formattedTime } = useEventListTimeFormatting(entry.schedulingData);

    const baseClassName = "kms-ds-slim-event-list-item";

    const theme = useTheme();
    const showThumb = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.sm));

    const showItemInfo = () => {
        setShowReadMoreModal(true);
        onShowInfo?.();
    };

    return (
        <ItemContainer className={baseClassName}>
            <ItemInfoAndActions>
                <div>
                    <DateLabel variant={"body2Highlight"} className={`chromatic-ignore ${baseClassName}-date-label`}>
                        {formattedDate} {formattedTime}
                    </DateLabel>

                    <TruncateWithTooltip text={entry.name} lines={2}>
                        <Typography component={"h3"} variant={"h5"} className={`${baseClassName}-entry-name`}>
                            {entry.name}
                        </Typography>
                    </TruncateWithTooltip>
                </div>

                <Actions className={`${baseClassName}-actions`}>
                    {callToActionButton && callToActionButton}
                    <EventListItemModal
                        data={entry}
                        show={showReadMoreModal}
                        onHide={() => setShowReadMoreModal(false)}
                        callToActionButton={callToActionButton}
                        inWatchlist={inWatchlist}
                        onToggleWatchList={onToggleWatchList}
                    />
                    <Tooltip title={translate("More info - %1", [entry.name])} placement={"bottom-start"}>
                        <ActionIconButton
                            variant={"borderless"}
                            onClick={showItemInfo}
                            size={"large"}
                            aria-label={translate("More info - %1", [entry.name])}
                            color={"secondary"}
                            className={`${baseClassName}-more-info-button`}
                        >
                            <Info24Icon />
                        </ActionIconButton>
                    </Tooltip>
                </Actions>
            </ItemInfoAndActions>
            {showThumb && <Thumbnail src={entry.thumbnailUrl} alt={""} />}
        </ItemContainer>
    );
};
